import { Collections, ListProduct, ScenarioName } from "@mindshift/datalayer";

import type { GridBox } from "@reco/shared/src/types/gridbox";

import { TrackingEventType } from "@reco/service/src/constants";
import { getPDPType } from "@reco/service/src/utils/pageType";
import { ABTestScenario } from "@reco/shared/src/types/abTest";

export type TrackingOptions = {
  pageType: string;
  recommendations: GridBox[];
  abTest?: ABTestScenario;
  extraParams?: Record<string, unknown>;
};

type ShopAsstViewTrackingParams = {
  gridboxes: GridBox[];
  promotionId?: string;
  creativeSlot?: string;
  gridboxType?: string;
}

/**
 * A unique identifier for the product team responsible for the tracking event.
 * This makes it easy for anyone to identify who to contact regarding updates or
 * fixes for this tracking implementation.
 */
const PRODUCT_TEAM = "RECO";
const VALID_EVENT_TYPES = new Set(Object.values(TrackingEventType));
const AVAILABILITY_MAP = {
  "3": "available",
  "2": "soon_available",
  "1": "not_available",
};

const SCENARIO_MAP: Record<string, Partial<Record<TrackingEventType, ScenarioName>>> = {
  pdp: {
    [TrackingEventType.PageLoad]: ScenarioName.PDP_Recommendation_Load,
    [TrackingEventType.EnterViewport]: ScenarioName.PDP_Recommendation_EnterViewport,
    [TrackingEventType.Click]: ScenarioName.General_Product_Click,
    [TrackingEventType.SponsoredRecommendationLoad]: ScenarioName.General_Product_Load,
  },
  default: {
    [TrackingEventType.PageLoad]: ScenarioName.General_Recommendation_Load,
    [TrackingEventType.EnterViewport]: ScenarioName.General_RecommendationProduct_EnterViewport,
    [TrackingEventType.Click]: ScenarioName.General_Product_Click,
  },
};

const SHOP_ASST_POSITION = 8;

export default class Tracking {
  static push(options: TrackingOptions, eventType: TrackingEventType): void {
    if (!VALID_EVENT_TYPES.has(eventType)) {
      return;
    }

    const collections = this.buildEvent(options, eventType);
    if (!collections.eventInfo?.eventAction) {
      return;
    }

    globalThis.dataLayerService = globalThis.dataLayerService || [];
    globalThis.dataLayerService.push({
      scenarioName: collections.eventInfo.eventAction,
      collections,
    });
  }

  static gridboxToProduct(gridbox: GridBox): ListProduct {
    return {
      brand: gridbox.brand?.name,
      type: getPDPType(gridbox.productType ?? ""),
      id: gridbox.erpNumber,
      parentId: gridbox.erpNumber,
      name: gridbox.keyfacts?.title,
      categoryPrimary: gridbox.keyfacts?.analyticsCategory,
      wonCategoryPrimary: gridbox.keyfacts?.wonCategoryPrimary,
      price: gridbox.price?.price,
      availability: AVAILABILITY_MAP[gridbox.stockAvailability?.availabilityIndicator],
      tradeType: gridbox.stockAvailability?.onlineAvailable ? "online" : "retail",
      position: gridbox.position,
      status: "recommendation-product",
      listName: gridbox.list,
      listId: gridbox.list,
      ratingAverage: gridbox.ratings?.average,
      sapId: gridbox.ians?.[0],
      adId: gridbox.advertisementId,
      currency: gridbox.price?.currencyCode,
      categoryPrimaryPath: gridbox.categorySecondaryPath,
    };
  }

  private static getScenarioName(pageType: string, eventType: string): ScenarioName | undefined {
    const pageScenarios = SCENARIO_MAP[pageType] || SCENARIO_MAP.default;
    return pageScenarios[eventType];
  }

  private static buildEvent(options: TrackingOptions, type: string): Collections {
    const { pageType, recommendations, abTest, extraParams } = options;
    const firstRecommendation = recommendations?.[0];

    return {
      application: {
        productTeam: PRODUCT_TEAM,
      },
      click: {
        linkName: firstRecommendation?.list,
        linkURL: firstRecommendation?.canonicalPath ?? undefined,
      },
      eventInfo: {
        eventAction: this.getScenarioName(pageType, type),
        eventCategory: type,
        eventInteractionType: type,
      },
      content: {
        personalisationAbTest: !!abTest,
        personalisationAbTestGroup: abTest?.group,
      },
      products: {
        list: recommendations.map(this.gridboxToProduct),
      },
      ...(extraParams && { extraParams }),
    };
  }

  static createShopAsstItem(promotionId?: string) {
    const promotionName = "AI Shopping Assistant Teaser";

    return {
      position: SHOP_ASST_POSITION,
      listId: "reco_pdp_products",
      listName: "reco_pdp_products",
      promotionId: promotionId ?? promotionName,
      promotionName,
    };
  }

  static trackShopAsstClick(promotionId?: string) {
    const payload = {
      scenarioName: "General_Product_Click",
      collections: {
        application: {
          productTeam: PRODUCT_TEAM,
        },
        click: {
          linkURL: "https://www.lidl.de/shopping-assistant",
        },
        content: {
          contentLabel: "AI Shopping Assistant Teaser",
        },
        products: {
          list: [Tracking.createShopAsstItem(promotionId)],
        },
      },
    };
    globalThis.dataLayerService?.push?.(payload);
  }

  static trackShopAsstView(params: ShopAsstViewTrackingParams) {
    const { gridboxes, promotionId, creativeSlot, gridboxType } = params;
    const list: Record<string, unknown>[] = gridboxes.slice(0, SHOP_ASST_POSITION - 1).map(Tracking.gridboxToProduct)
      .map(item => ({ creativeSlot, gridBoxType: gridboxType, ...item }));
    list.push(Tracking.createShopAsstItem(promotionId));

    const payload = {
      scenarioName: "General_Product_View",
      collections: {
        products: {
          list,
        },
      },
    };
    globalThis.dataLayerService?.push?.(payload);
  }
}
